import { Typography } from "@mui/material";
import { get } from "lodash";
import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import AppBar from "src/components/AppBar/AppBar";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import { IconName } from "src/components/Icon/Icon";
import FilterContainer from "src/components/TableV2/FilterContainer";
import { TableActions } from "src/components/TableV2/TableActions";
import Title from "src/components/TableV2/Title";
import DisTable from "../../components/TableV2/Table";
import * as S from "../../constants/StringConstants";
import { IFileUploadRecord } from "../../store/models/fileUpload.model";
import { IRootState } from "../../store/reducers";
import {
  deleteSecureDocument,
  downloadSecureDocument,
  fetchUnreadFilesCount,
  fetchUploadedFiles,
} from "../../store/reducers/secureUpload";
import { saveToDownloads } from "../../util/util.fileService";
import "./FileUpload.scss";

export interface IFileUploadTableAllProps extends StateProps, DispatchProps, RouteComponentProps {}

interface IState {
  deletedId: number | null;
}

export class FileUpload extends React.Component<IFileUploadTableAllProps, IState> {
  fileTableRef: any;
  state: IState = { deletedId: null };

  async componentDidMount() {}

  downloadDocument = async (document: IFileUploadRecord) => {
    const result = await this.props.downloadSecureDocument(document.secureUploadID);

    if (result) {
      await this.props.fetchUnreadFilesCount();
      this.fileTableRef?.update?.();
    }

    const fileName = get(result, "value.headers.x-attachment-filename");
    const data = get(result, "value.data");
    if (fileName && data) {
      saveToDownloads(fileName, "application/pdf", data);
    }
  };

  openDeletePrompt = (secureUploadID: number) => {
    this.setState({ deletedId: secureUploadID });
  };

  handleDeleteConfirm = async () => {
    const { deletedId } = this.state;
    if (deletedId == null) return;

    try {
      await this.props.deleteSecureDocument(deletedId);
      await this.props.fetchUnreadFilesCount();
      this.fileTableRef?.update?.();
    } finally {
      this.setState({ deletedId: null });
    }
  };

  handleDeleteCancel = () => {
    this.setState({ deletedId: null });
  };

  render() {
    const {
      fetchUploadedFiles,
      SecureUploadState: { map, projection, total, listOptions, loading },
    } = this.props;

    const columns = [
      {
        key: "isUnread",
        title: S.SU_STATUS,
        style: { minWidth: "8rem", paddingRight: "2rem", textAlign: "center" },
        render: item => {
          return item.isUnread ? <span className="new-indicator">{S.SU_STATUS_NEW}</span> : null;
        },
      },
      {
        key: "author",
        title: S.SU_AUTHOR,
        style: { minWidth: "15rem" },
      },
      {
        key: "category",
        title: S.SU_CATEGORY,
        style: { minWidth: "16rem" },
      },
      {
        key: "fileName",
        title: S.SU_FILE,
        style: { minWidth: "2rem" },
      },
      {
        key: "uploadDate",
        title: S.SU_UPLOAD_DATE,
        style: { minWidth: "18rem" },
      },
      {
        key: "note",
        title: S.SU_NOTE,
        style: { minWidth: "12rem" },
      },
      {
        key: "actions",
        title: S.SU_ACTIONS,
        style: { minWidth: "10rem" },
        render: (item: IFileUploadRecord) =>
          item.deleted ? (
            <span>{S.SU_DELETED}</span>
          ) : (
            <TableActions
              actions={[
                {
                  label: S.DOC_DOWNLOAD_BUTTON,
                  handler: () => this.downloadDocument(item),
                },
                {
                  label: S.DOC_DELETE_BUTTON,
                  handler: () => this.openDeletePrompt(item.secureUploadID),
                },
              ]}
            />
          ),
      },
    ];

    return (
      <div className="table-view-v2__content">
        <div className="table-view__header-buttons-container file-upload-header">
          <AppBar icon={IconName.FileUpload} title={S.FILE_PAGE_TITLE} loading={loading} />
        </div>

        <DisTable<IFileUploadRecord>
          columns={columns}
          initialState={{ rows: 25, map, projection, total, ...listOptions }}
          getRef={ft => {
            this.fileTableRef = ft;
          }}
          onUpdate={async ({ page, rows, sort, query }) => {
            const result: any = await fetchUploadedFiles({ page, rows, sort, query });
            return {
              total: get(result, "value.data.totalRows") as number,
              data: get(result, "value.data.items") as IFileUploadRecord[],
            };
          }}>
          <FilterContainer left={<Title text={S.FILE_PAGE_TITLE} />}></FilterContainer>
        </DisTable>

        {this.state.deletedId !== null && (
          <PromptModal
            open={true}
            title={S.DOC_CONFIRM_TITLE}
            confirmText={S.BRO_COMMISSIONS_DELETE_CONFIRM_CONFIRM}
            rejectText={S.BRO_COMMISSIONS_DELETE_CONFIRM_REJECT}
            onConfirm={this.handleDeleteConfirm}
            onReject={this.handleDeleteCancel}>
            <Typography variant="h6">{S.DOC_DELETE_CONFIRM_TEXT}</Typography>
          </PromptModal>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ SecureUploadState, proposalOptions: { statusList } }: IRootState) => ({
  SecureUploadState,
  statusList,
});

const mapDispatchToProps = {
  fetchUploadedFiles,
  downloadSecureDocument,
  deleteSecureDocument,
  fetchUnreadFilesCount,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(FileUpload);
